import { useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsCaveat } from 'components/common/ns-caveat/NsCaveat';
import {
  selectDetails,
  selectDirectDebitErrors,
  selectIsLoadingDirectDebit,
  selectHasMaxAttemptsError,
  setDirectDebitField,
  resetDetails as resetDirectDebitDetails
} from 'state/direct-debit/direct-debit-slice';
import { getDirectDebitPaymentTokenThunk } from 'state/direct-debit/direct-debit-thunks';
import { DirectDebitSummary } from 'components/direct-sales/direct-debit/components/DirectDebitSummary';
import { DirectDebitForm } from 'components/direct-sales/direct-debit/components/DirectDebitForm';
import { DirectDebitGuarantee } from 'components/direct-sales/direct-debit/components/DirectDebitGuarantee';
import { DirectDebitCompanyForm } from 'components/direct-sales/direct-debit/components/DirectDebitCompanyForm';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { useValidateBankAccount } from 'components/direct-sales/direct-debit/hooks/useValidateBankAccount';
import { useDirectDebitVerificationError } from 'components/direct-sales/direct-debit/hooks/useDirectDebitVerificationError';
import { useNavigation } from 'hooks/useNavigation';
import { usePartner } from 'components/app/routes/usePartner';
import { DirectDebitPromotionExpander } from './components/DirectDebitPromotionExpander';
import { useStateMachineContext } from 'machines/hooks/useStateMachineContext';

export const testId = 'directDebitTestId';
export const ddCaveatTestId = 'ddCaveatTestId';

export const DirectDebit = () => {
  const { actor } = useStateMachineContext();
  const dispatch = useDispatch();
  const { isDSJourney } = usePartner();
  const errors = useSelector(selectDirectDebitErrors, shallowEqual);
  const isLoading = useSelector(selectIsLoadingDirectDebit, shallowEqual);
  const details = useSelector(selectDetails, shallowEqual);
  const hasMaxAttemptsError = useSelector(
    selectHasMaxAttemptsError,
    shallowEqual
  );

  const { isCommercial, accountHolderName, sortCode, accountNumber } = details;
  const validateBankAccount = useValidateBankAccount();
  const handleDirectDebitVerificationError = useDirectDebitVerificationError();
  const { eventTypes, next } = useNavigation();

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (hasMaxAttemptsError) {
        return;
      }

      try {
        await validateBankAccount();

        const bankAccountData = {
          sortCode: sortCode,
          accountNumber: accountNumber,
          accountHolder: accountHolderName
        };
        await dispatch(getDirectDebitPaymentTokenThunk(bankAccountData));
        next({ eventType: eventTypes.NEXT });
      } catch (error) {
        const e = handleDirectDebitVerificationError(error);
        e && console.error(e);
      }
    },
    [
      dispatch,
      validateBankAccount,
      handleDirectDebitVerificationError,
      hasMaxAttemptsError,
      sortCode,
      accountNumber,
      accountHolderName,
      next,
      eventTypes.NEXT
    ]
  );

  const handleChange = useCallback(
    (event) => {
      const {
        target: { name, processedValue: value }
      } = event;

      dispatch(setDirectDebitField({ name, value }));
    },
    [dispatch]
  );

  // BUG 153994: reset direct debit details on back navigation.
  // `actor` will be recreated on back navigation
  useEffect(() => {
    dispatch(resetDirectDebitDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor]);

  return useMemo(() => {
    if (isLoading) {
      return <NsStopgap />;
    }

    return (
      <>
        <NsPanel testId={testId}>
          <NsBackCta />
          <DirectDebitSummary />
          <NsContent>
            <NsTitle title="Payment details" id="paymentDetails" />
            <p>
              To set up your Direct Debit, please fill in your details exactly
              as they appear on your bank account.
            </p>
          </NsContent>
          <NsForm onSubmit={handleSubmit}>
            {!isCommercial ? (
              <DirectDebitForm handleChange={handleChange} details={details} />
            ) : (
              <DirectDebitCompanyForm
                handleChange={handleChange}
                details={details}
              />
            )}
            <DirectDebitGuarantee />
            <Error errors={errors} />
            <NsPrimaryCta isLoading={isLoading}>Continue</NsPrimaryCta>
          </NsForm>
          {isDSJourney && <DirectDebitPromotionExpander />}
        </NsPanel>
        <NsCaveat heading="Legal information" testId={ddCaveatTestId} />
      </>
    );
  }, [
    handleSubmit,
    handleChange,
    errors,
    isLoading,
    isCommercial,
    details,
    isDSJourney
  ]);
};

export default DirectDebit;
